import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';

import { messages } from './utils/messages.ts';
import { useRecentlyUpdatedItems } from './useRecentlyUpdatedItems.ts';
import type { RecentlyUpdatedMacroParams, UpdateItem } from './utils/recently-updated-types.ts';
import { resolveTheme, groupByAuthor, filterByContentType } from './utils/itemTransforms.ts';
import { ContentTypeFilter } from './components/ContentTypeFilter.tsx';
import { FlatUpdateItem, SidebarUpdateItem } from './components/UpdateItems.tsx';
import { SocialGroup } from './components/SocialGroup.tsx';
import { ShowMoreButton } from './components/ShowMoreButton.tsx';

const styles = cssMap({
	container: {
		color: token('color.text'),
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		overflow: 'auto',
		paddingBlock: token('space.200'),
	},
	heading: {
		font: token('font.heading.medium'),
		color: token('color.text'),
		marginBlockStart: token('space.0'),
		marginBlockEnd: token('space.200'),
	},
	list: {
		listStyle: 'disc',
		paddingInlineStart: token('space.300'),
		margin: token('space.0'),
	},
	sidebarList: {
		listStyle: 'none',
		padding: token('space.0'),
		margin: token('space.0'),
	},
	showMoreContainer: {
		marginBlockStart: token('space.200'),
		paddingBlockStart: token('space.100'),
		borderBlockStartWidth: token('border.width'),
		borderBlockStartStyle: 'solid',
		borderColor: token('color.border'),
		display: 'flex',
		justifyContent: 'flex-start',
	},
	groupList: {
		listStyle: 'none',
		padding: token('space.0'),
		margin: token('space.0'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
	emptyState: {
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		paddingBlock: token('space.200'),
	},
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type RecentlyUpdatedMacroHandlerProps = {
	macroDefaultProps?: Record<string, unknown>;
	macroParams?: RecentlyUpdatedMacroParams;
};

type RecentlyUpdatedMacroRendererProps = RecentlyUpdatedMacroParams & {
	macroDefaultProps?: Record<string, unknown>;
};

function isExtensionHandlerProps(
	value: Record<string, unknown> | undefined,
): value is ExtensionHandlerProps {
	return Boolean(
		value &&
		typeof value.mode === 'string' &&
		typeof value.contentId === 'string' &&
		typeof value.extensionKey === 'string' &&
		value.node,
	);
}

function getParamRawValue<T>(raw: unknown): T | undefined {
	if (raw && typeof raw === 'object' && 'value' in raw) {
		return (raw as { value?: T }).value;
	}
	return raw as T | undefined;
}

function getParamValue<T>(
	macroParams: Record<string, unknown> | undefined,
	macroDefaultProps: Record<string, unknown> | undefined,
	key: string,
): T | undefined {
	const explicitRaw = macroParams?.[key];
	if (explicitRaw !== undefined) {
		return getParamRawValue<T>(explicitRaw);
	}
	return getParamRawValue<T>(macroDefaultProps?.[key]);
}

function resolveBoolean(value: unknown): boolean | undefined {
	if (value === undefined || value === null || value === '') return undefined;
	if (typeof value === 'boolean') return value;
	if (typeof value === 'string') {
		if (value.toLowerCase() === 'true') return true;
		if (value.toLowerCase() === 'false') return false;
	}
	return Boolean(value);
}

function resolveParams(
	macroParams: Record<string, unknown> | undefined,
	macroDefaultProps: Record<string, unknown> | undefined,
): RecentlyUpdatedMacroParams {
	const maxCandidate = getParamValue<unknown>(macroParams, macroDefaultProps, 'max');
	const maxParsed = Number(maxCandidate);

	return {
		theme: getParamValue<RecentlyUpdatedMacroParams['theme']>(
			macroParams,
			macroDefaultProps,
			'theme',
		),
		showProfilePic: resolveBoolean(
			getParamValue<unknown>(macroParams, macroDefaultProps, 'showProfilePic'),
		),
		spaces: getParamValue<string>(macroParams, macroDefaultProps, 'spaces'),
		types: getParamValue<string>(macroParams, macroDefaultProps, 'types'),
		author: getParamValue<string>(macroParams, macroDefaultProps, 'author'),
		labels: getParamValue<string>(macroParams, macroDefaultProps, 'labels'),
		max: Number.isFinite(maxParsed) && maxParsed > 0 ? Math.floor(maxParsed) : undefined,
		width: getParamValue<string>(macroParams, macroDefaultProps, 'width'),
		hideHeading: resolveBoolean(
			getParamValue<unknown>(macroParams, macroDefaultProps, 'hideHeading'),
		),
		currentSpaceKey: getParamValue<string>(macroParams, macroDefaultProps, 'currentSpaceKey'),
	};
}

function resolveMaxItems(max: RecentlyUpdatedMacroParams['max']): number {
	const parsed = Number(max);
	return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 15;
}

export function RecentlyUpdatedMacroRendererHandler({
	macroDefaultProps,
	macroParams,
}: RecentlyUpdatedMacroHandlerProps): React.JSX.Element {
	const resolvedParams = resolveParams(
		macroParams as Record<string, unknown> | undefined,
		macroDefaultProps,
	);
	return <RecentlyUpdatedMacroRenderer {...resolvedParams} macroDefaultProps={macroDefaultProps} />;
}

export function RecentlyUpdatedMacroRenderer({
	macroDefaultProps,
	...params
}: RecentlyUpdatedMacroRendererProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const theme = resolveTheme(params);
	const trackingProps = isExtensionHandlerProps(macroDefaultProps)
		? {
				contentId: macroDefaultProps.contentId,
				extensionKey: macroDefaultProps.extensionKey,
				mode: macroDefaultProps.mode,
				attributes:
					macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(macroDefaultProps.node),
				name: getExperienceName(macroDefaultProps.mode, macroDefaultProps.node),
			}
		: null;

	const [cursor, setCursor] = useState<string | null>(null);

	const {
		items: pageItems,
		loading,
		error,
		hasNextPage,
		endCursor,
	} = useRecentlyUpdatedItems(params, cursor);

	// Accumulate items across pages synchronously during render (not in useEffect) so
	// that items are available on the very first render without an extra re-render cycle.
	const accumulatedItemsRef = useRef<UpdateItem[]>([]);
	const prevCursorRef = useRef<string | null>(null);

	if (!loading) {
		if (cursor === null) {
			// Params reset or first load — replace accumulated items with the first page
			accumulatedItemsRef.current = pageItems;
		} else if (cursor !== prevCursorRef.current) {
			// New cursor page loaded — append items as returned by the query
			accumulatedItemsRef.current = [...accumulatedItemsRef.current, ...pageItems];
		}
		prevCursorRef.current = cursor;
	}

	const itemsForRender = accumulatedItemsRef.current;

	const max = resolveMaxItems(params.max);
	const [visibleCount, setVisibleCount] = useState(max);

	// When key params change, reset to a fresh first-page fetch
	useEffect(() => {
		setCursor(null);
		accumulatedItemsRef.current = [];
		prevCursorRef.current = null;
		setVisibleCount(max);
	}, [max, params.spaces, params.types, params.labels, params.author, params.currentSpaceKey]);

	const handleShowMore = useCallback(() => {
		setVisibleCount((prev) => prev + max);
		// If we're close to exhausting buffered items, fetch the next GraphQL page
		if (endCursor && itemsForRender.length <= visibleCount + max) {
			setCursor(endCursor);
		}
	}, [endCursor, itemsForRender.length, max, visibleCount]);

	const [selectedContentType, setSelectedContentType] = useState('');

	type SelectOption = { value: string; label: string };

	const contentTypeOptions: SelectOption[] = useMemo(
		() => [
			{ value: '', label: formatMessage(messages.filterAll) },
			{ value: 'page', label: formatMessage(messages.filterPages) },
			{ value: 'blogpost', label: formatMessage(messages.filterBlogPosts) },
			{ value: 'comment', label: formatMessage(messages.filterComments) },
			{ value: 'attachment', label: formatMessage(messages.filterAttachments) },
			{ value: 'whiteboard', label: formatMessage(messages.filterWhiteboards) },
			{ value: 'database', label: formatMessage(messages.filterDatabases) },
			{ value: 'embed', label: formatMessage(messages.filterEmbeds) },
		],
		[formatMessage],
	);

	const handleContentTypeChange = useCallback((val: string) => {
		setSelectedContentType(val);
	}, []);

	const filteredItems = useMemo(
		() => filterByContentType(itemsForRender, selectedContentType),
		[itemsForRender, selectedContentType],
	);

	const visibleItems = filteredItems.slice(0, visibleCount);

	const hasMore = visibleCount < filteredItems.length || hasNextPage;

	const showHeading = !params.hideHeading || visibleItems.length === 0;
	const showFilter = theme === 'social';

	if (loading && itemsForRender.length === 0) {
		return (
			<Box xcss={styles.container} testId="recently-updated-macro">
				<Box as="h2" xcss={styles.heading} testId="recently-updated-heading">
					{formatMessage(messages.heading)}
				</Box>
				<Box xcss={styles.emptyState} testId="recently-updated-loading">
					{formatMessage(messages.loadingLabel)}
				</Box>
			</Box>
		);
	}

	if (error) {
		return (
			<Box xcss={styles.container} testId="recently-updated-macro">
				{trackingProps && <MacroExperienceFailure {...trackingProps} error={error} />}
				<Box xcss={styles.emptyState} testId="recently-updated-error">
					{formatMessage(messages.errorMessage)}
				</Box>
			</Box>
		);
	}

	if (!loading && visibleItems.length === 0) {
		return (
			<Box xcss={styles.container} testId="recently-updated-macro">
				{trackingProps && <MacroExperienceSuccess {...trackingProps} />}
				{showHeading && (
					<Box as="h2" xcss={styles.heading} testId="recently-updated-heading">
						{formatMessage(messages.heading)}
					</Box>
				)}
				<Box xcss={styles.emptyState} testId="recently-updated-empty">
					{formatMessage(messages.noUpdates)}
				</Box>
			</Box>
		);
	}

	let itemList;
	if (theme === 'social') {
		itemList = (
			<Box as="ul" xcss={styles.groupList} testId="recently-updated-social-list">
				{groupByAuthor(visibleItems).map((group, index) => (
					<SocialGroup
						key={
							group.author.username
								? `${group.author.username}-${group.items[0].id}`
								: `group-${index}`
						}
						group={group}
					/>
				))}
			</Box>
		);
	} else if (theme === 'sidebar') {
		itemList = (
			<Box as="ul" xcss={styles.sidebarList} testId="recently-updated-list">
				{visibleItems.map((item) => (
					<SidebarUpdateItem key={item.id} item={item} />
				))}
			</Box>
		);
	} else {
		itemList = (
			<Box as="ul" xcss={styles.list} testId="recently-updated-list">
				{visibleItems.map((item) => (
					<FlatUpdateItem key={item.id} item={item} />
				))}
			</Box>
		);
	}

	return (
		<Box xcss={styles.container} testId="recently-updated-macro">
			{trackingProps && <MacroExperienceSuccess {...trackingProps} />}
			{showHeading && (
				<Box as="h2" xcss={styles.heading} testId="recently-updated-heading">
					{formatMessage(messages.heading)}
				</Box>
			)}

			{showFilter && (
				<ContentTypeFilter
					value={selectedContentType}
					onChange={handleContentTypeChange}
					options={contentTypeOptions}
					filterLabel={formatMessage(messages.filterLabel)}
				/>
			)}

			{itemList}

			{hasMore && (
				<Box xcss={styles.showMoreContainer}>
					<ShowMoreButton
						label={formatMessage(messages.showMore)}
						ariaLabel={formatMessage(messages.showMoreAriaLabel)}
						onClick={handleShowMore}
						loading={loading}
					/>
				</Box>
			)}
		</Box>
	);
}
